/**
 * CommentPanel component for displaying and managing essay comments.
 *
 * Features:
 * - Displays all comments grouped by type (inline, paragraph, general)
 * - Threaded reply display with indentation
 * - Reply input for adding responses
 * - Resolve/unresolve buttons (author only)
 * - Edit/delete buttons (owner/author only)
 *
 * Called by:
 * - EssayCommentsPage as the full-page comments view
 */
import { useState, useCallback } from 'react';
import {
  Typography,
  Space,
  Avatar,
  Button,
  Input,
  Divider,
  Tag,
  Tooltip,
  Empty,
  Collapse,
  message,
  Popconfirm,
} from 'antd';
import {
  UserOutlined,
  CommentOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
  CloseOutlined,
  HighlightOutlined,
  AlignLeftOutlined,
  MessageOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { CommentWithReplies, CreateCommentRequest, User } from '@compendium/shared';
import {
  createComment,
  updateComment,
  deleteComment,
  resolveComment,
  unresolveComment,
} from '../services/api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// =============================================================================
// Props Interface
// =============================================================================

interface CommentPanelProps {
  /** Essay slug for API calls */
  essaySlug: string;
  /** List of comments to display */
  comments: CommentWithReplies[];
  /** Current authenticated user */
  currentUser: User | null;
  /** Callback when comments are modified (to refresh list) */
  onCommentsChange: () => void;
  /** Callback when clicking an inline comment to scroll to it */
  onCommentClick?: (comment: CommentWithReplies) => void;
}

// =============================================================================
// Helper Components
// =============================================================================

/**
 * Format a date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Get icon for comment type.
 */
function getTypeIcon(type: string) {
  switch (type) {
    case 'inline':
      return <HighlightOutlined />;
    case 'paragraph':
      return <AlignLeftOutlined />;
    default:
      return <MessageOutlined />;
  }
}

/**
 * Get label for comment type.
 */
function getTypeLabel(type: string): string {
  switch (type) {
    case 'inline':
      return 'Inline';
    case 'paragraph':
      return 'Paragraph';
    default:
      return 'General';
  }
}

// =============================================================================
// Single Comment Component
// =============================================================================

interface CommentItemProps {
  comment: CommentWithReplies;
  currentUser: User | null;
  essaySlug: string;
  onCommentsChange: () => void;
  onCommentClick?: (comment: CommentWithReplies) => void;
  depth?: number;
  isTopLevel?: boolean;
}

/**
 * Renders a single comment with its replies recursively.
 */
function CommentItem({
  comment,
  currentUser,
  essaySlug,
  onCommentsChange,
  onCommentClick,
  depth = 0,
  isTopLevel = false,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check permissions
  const isOwner = currentUser?.id === comment.userId;
  const isAuthor = currentUser?.role === 'author';
  const canEdit = isOwner;
  const canDelete = isOwner || isAuthor;
  const canResolve = isAuthor && isTopLevel;

  // Handle edit save
  const handleSaveEdit = useCallback(async () => {
    if (!editContent.trim()) return;

    setIsLoading(true);
    try {
      await updateComment(comment.id, editContent.trim());
      message.success('Comment updated');
      setIsEditing(false);
      onCommentsChange();
    } catch (error) {
      message.error('Failed to update comment');
      console.error('Error updating comment:', error);
    } finally {
      setIsLoading(false);
    }
  }, [comment.id, editContent, onCommentsChange]);

  // Handle reply submit
  const handleSubmitReply = useCallback(async () => {
    if (!replyContent.trim()) return;

    setIsLoading(true);
    try {
      const replyData: CreateCommentRequest = {
        type: comment.type,
        content: replyContent.trim(),
        parentId: comment.id,
        // Inherit position from parent for inline/paragraph comments
        selectionStart: comment.selectionStart ?? undefined,
        selectionEnd: comment.selectionEnd ?? undefined,
        paragraphIndex: comment.paragraphIndex ?? undefined,
      };

      await createComment(essaySlug, replyData);
      message.success('Reply added');
      setReplyContent('');
      setIsReplying(false);
      onCommentsChange();
    } catch (error) {
      message.error('Failed to add reply');
      console.error('Error adding reply:', error);
    } finally {
      setIsLoading(false);
    }
  }, [comment, essaySlug, replyContent, onCommentsChange]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    try {
      await deleteComment(comment.id);
      message.success('Comment deleted');
      onCommentsChange();
    } catch (error) {
      message.error('Failed to delete comment');
      console.error('Error deleting comment:', error);
    } finally {
      setIsLoading(false);
    }
  }, [comment.id, onCommentsChange]);

  // Handle resolve/unresolve
  const handleToggleResolve = useCallback(async () => {
    setIsLoading(true);
    try {
      if (comment.resolved) {
        await unresolveComment(comment.id);
        message.success('Comment reopened');
      } else {
        await resolveComment(comment.id);
        message.success('Comment resolved');
      }
      onCommentsChange();
    } catch (error) {
      message.error('Failed to update comment status');
      console.error('Error toggling resolve:', error);
    } finally {
      setIsLoading(false);
    }
  }, [comment.id, comment.resolved, onCommentsChange]);

  // Handle click on inline comment
  const handleClick = useCallback(() => {
    if (comment.type === 'inline' && onCommentClick) {
      onCommentClick(comment);
    }
  }, [comment, onCommentClick]);

  return (
    <div
      style={{
        marginLeft: depth * 16,
        opacity: comment.resolved ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      <div
        style={{
          padding: '12px',
          background: comment.flagged ? 'var(--bg-danger-tint)' : (depth === 0 ? 'var(--bg-card)' : 'var(--bg-card-nested)'),
          borderRadius: '8px',
          marginBottom: '8px',
          border: comment.flagged ? `1px solid var(--color-danger)` : (comment.resolved ? '1px solid var(--border-primary)' : '1px solid transparent'),
          cursor: comment.type === 'inline' ? 'pointer' : 'default',
        }}
        onClick={handleClick}
      >
        {/* Flagged banner (visible to author only) */}
        {comment.flagged && isAuthor && (
          <div style={{ marginBottom: '8px' }}>
            <Tag color="red" icon={<ExclamationCircleOutlined />}>
              Flagged: {comment.flagReason || 'unknown'}
            </Tag>
          </div>
        )}

        {/* Comment header */}
        <Space size="small" style={{ marginBottom: '8px', width: '100%', justifyContent: 'space-between' }}>
          <Space size="small">
            <Avatar
              src={comment.user.avatarUrl}
              icon={!comment.user.avatarUrl && <UserOutlined />}
              size="small"
            />
            <Text strong style={{ color: '#fff' }}>{comment.user.name}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatDate(comment.createdAt)}
            </Text>
          </Space>

          {isTopLevel && (
            <Space size={4}>
              <Tag
                icon={getTypeIcon(comment.type)}
                style={{ margin: 0, fontSize: '11px' }}
              >
                {getTypeLabel(comment.type)}
              </Tag>
              {comment.resolved && (
                <Tag color="success" style={{ margin: 0, fontSize: '11px' }}>
                  Resolved
                </Tag>
              )}
            </Space>
          )}
        </Space>

        {/* Comment content */}
        {isEditing ? (
          <div style={{ marginBottom: '8px' }}>
            <TextArea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              autoSize={{ minRows: 2 }}
              style={{ marginBottom: '8px' }}
            />
            <Space>
              <Button
                size="small"
                type="primary"
                onClick={handleSaveEdit}
                loading={isLoading}
                disabled={!editContent.trim()}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(comment.content);
                }}
              >
                Cancel
              </Button>
            </Space>
          </div>
        ) : (
          <Paragraph
            style={{
              color: '#e0e0e0',
              marginBottom: '8px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {comment.content}
          </Paragraph>
        )}

        {/* Comment actions */}
        {!isEditing && currentUser && (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<CommentOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setIsReplying(!isReplying);
              }}
            >
              Reply
            </Button>

            {canEdit && (
              <Tooltip title="Edit">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                />
              </Tooltip>
            )}

            {canDelete && (
              <Popconfirm
                title="Delete comment?"
                description="This will also delete all replies."
                onConfirm={(e) => {
                  e?.stopPropagation();
                  handleDelete();
                }}
                onCancel={(e) => e?.stopPropagation()}
                okText="Delete"
                cancelText="Cancel"
              >
                <Tooltip title="Delete">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Popconfirm>
            )}

            {canResolve && (
              <Tooltip title={comment.resolved ? 'Reopen' : 'Resolve'}>
                <Button
                  type="text"
                  size="small"
                  icon={comment.resolved ? <UndoOutlined /> : <CheckCircleOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleResolve();
                  }}
                  loading={isLoading}
                />
              </Tooltip>
            )}
          </Space>
        )}

        {/* Reply input */}
        {isReplying && (
          <div style={{ marginTop: '12px' }} onClick={(e) => e.stopPropagation()}>
            <TextArea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              autoSize={{ minRows: 2 }}
              style={{ marginBottom: '8px' }}
            />
            <Space>
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSubmitReply}
                loading={isLoading}
                disabled={!replyContent.trim()}
              >
                Reply
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setIsReplying(false);
                  setReplyContent('');
                }}
              >
                Cancel
              </Button>
            </Space>
          </div>
        )}
      </div>

      {/* Render replies recursively */}
      {comment.replies.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              currentUser={currentUser}
              essaySlug={essaySlug}
              onCommentsChange={onCommentsChange}
              onCommentClick={onCommentClick}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * CommentPanel component.
 * Displays all comments for an essay as an inline panel (no drawer).
 */
function CommentPanel({
  essaySlug,
  comments,
  currentUser,
  onCommentsChange,
  onCommentClick,
}: CommentPanelProps) {
  const [newCommentContent, setNewCommentContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Group comments by type
  const inlineComments = comments.filter((c) => c.type === 'inline');
  const paragraphComments = comments.filter((c) => c.type === 'paragraph');
  const generalComments = comments.filter((c) => c.type === 'general');

  // Handle new general comment submission
  const handleSubmitGeneralComment = useCallback(async () => {
    if (!newCommentContent.trim()) return;

    setIsSubmitting(true);
    try {
      await createComment(essaySlug, {
        type: 'general',
        content: newCommentContent.trim(),
      });
      message.success('Comment added');
      setNewCommentContent('');
      onCommentsChange();
    } catch (error) {
      message.error('Failed to add comment');
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [essaySlug, newCommentContent, onCommentsChange]);

  // Build collapse items for each comment type
  const collapseItems = [];

  if (inlineComments.length > 0) {
    collapseItems.push({
      key: 'inline',
      label: (
        <Space>
          <HighlightOutlined />
          <span>Inline Comments</span>
          <Tag>{inlineComments.length}</Tag>
        </Space>
      ),
      children: (
        <div>
          {inlineComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUser={currentUser}
              essaySlug={essaySlug}
              onCommentsChange={onCommentsChange}
              onCommentClick={onCommentClick}
              isTopLevel
            />
          ))}
        </div>
      ),
    });
  }

  if (paragraphComments.length > 0) {
    collapseItems.push({
      key: 'paragraph',
      label: (
        <Space>
          <AlignLeftOutlined />
          <span>Paragraph Comments</span>
          <Tag>{paragraphComments.length}</Tag>
        </Space>
      ),
      children: (
        <div>
          {paragraphComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUser={currentUser}
              essaySlug={essaySlug}
              onCommentsChange={onCommentsChange}
              onCommentClick={onCommentClick}
              isTopLevel
            />
          ))}
        </div>
      ),
    });
  }

  if (generalComments.length > 0) {
    collapseItems.push({
      key: 'general',
      label: (
        <Space>
          <MessageOutlined />
          <span>General Comments</span>
          <Tag>{generalComments.length}</Tag>
        </Space>
      ),
      children: (
        <div>
          {generalComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUser={currentUser}
              essaySlug={essaySlug}
              onCommentsChange={onCommentsChange}
              isTopLevel
            />
          ))}
        </div>
      ),
    });
  }

  return (
    <div>
      {/* New general comment input */}
      {currentUser && (
        <div style={{ marginBottom: '16px' }}>
          <TextArea
            value={newCommentContent}
            onChange={(e) => setNewCommentContent(e.target.value)}
            placeholder="Add a general comment..."
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ marginBottom: '8px' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmitGeneralComment}
            loading={isSubmitting}
            disabled={!newCommentContent.trim()}
          >
            Add Comment
          </Button>
        </div>
      )}

      <Divider style={{ margin: '16px 0', borderColor: 'var(--border-primary)' }} />

      {/* Comments list */}
      {comments.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary">
              No comments yet. Select text in the essay to add an inline comment, or use the form above for general comments.
            </Text>
          }
        />
      ) : (
        <Collapse
          items={collapseItems}
          defaultActiveKey={['inline', 'paragraph', 'general']}
          ghost
          style={{ background: 'transparent' }}
        />
      )}
    </div>
  );
}

export default CommentPanel;
